// The x-ray: this code, redrawn from the sampled grid, with what every square
// is for and the order a reader walks them.
//
// **CANVAS, NOT DOM.** A version 40 code is 177 by 177, which is 31,329
// squares; that many elements would crawl on a phone. The cost is that none of
// this is reachable by a screen reader, so the canvas is aria-hidden and every
// fact it shows is also stated as text above it. The picture is the
// explanation, not the information.

import { ROLE } from './grid.js';

// **Layer colours are passed in from the page, never written here.** Tailwind
// cannot see this file (I-80), and more importantly these must come from the
// theme so they hold in both light and dark.
const LAYERS = [
    { role: ROLE.FINDER, name: 'Finder patterns',
      why: 'The three big squares. A reader finds these first, and their absence at the fourth corner is how it works out which way up the code is.' },
    { role: ROLE.TIMING, name: 'Timing lines',
      why: 'One row and one column of alternating squares, which is the ruler a reader counts modules against.' },
    { role: ROLE.ALIGNMENT, name: 'Alignment patterns',
      why: 'Smaller squares that let a reader correct for the code being photographed at an angle or printed on something curved.' },
    { role: ROLE.FORMAT, name: 'Format information',
      why: 'How much error correction was used and which mask was applied. Stored twice, so a damaged corner cannot lose it.' },
    { role: ROLE.VERSION, name: 'Version information',
      why: 'Only on larger codes, and also stored twice. Small codes do not need it because the size alone gives the version away.' },
    { role: ROLE.SEPARATOR, name: 'Separators',
      why: 'The blank border around each finder pattern, which stops it blurring into the data next to it.' },
    { role: ROLE.DATA, name: 'The data',
      why: 'Everything left over. This is your answer, plus the error correction that lets it survive damage.' },
];

export function layers() { return LAYERS; }

function paint(canvas, grid, roles, options) {
    const size = grid.size;
    const scale = Math.max(1, Math.floor(options.pixels / size));
    const edge = scale * size;

    canvas.width = edge;
    canvas.height = edge;
    canvas.style.width = '100%';
    canvas.style.height = 'auto';

    const context = canvas.getContext('2d');
    context.fillStyle = options.light;
    context.fillRect(0, 0, edge, edge);

    // The code as it actually is, at full contrast.
    context.fillStyle = options.dark;
    for (let row = 0; row < size; row++) {
        for (let column = 0; column < size; column++) {
            if (grid.modules[(row * size) + column]) {
                context.fillRect(column * scale, row * scale, scale, scale);
            }
        }
    }

    if (options.highlight === null) { return; }

    // ------------------------------------------------------------------
    // **SPOTLIGHT, NOT TINT, and the first version got this wrong.**
    //
    // Tinting the lit layer meant its appearance depended on whether its
    // squares happened to be dark: layers with a mix showed solid accent
    // squares with gaps, while SEPARATORS, which are light squares by
    // definition and the only such layer, came out as one uniform slab of
    // 35% indigo and read as a dark blob rather than as a highlight.
    //
    // Washing out everything ELSE toward the page background is identical
    // for every layer, keeps the lit region showing the code's own true
    // appearance, and works in both themes without a single colour
    // decision: the wash is the background, so it lightens on light and
    // darkens on dark. It also sidesteps the compositing trap entirely,
    // because no colour is ever painted ON TOP of a module.
    // ------------------------------------------------------------------
    context.fillStyle = options.light;
    context.globalAlpha = 0.82;
    for (let row = 0; row < size; row++) {
        for (let column = 0; column < size; column++) {
            if (roles[(row * size) + column] === options.highlight) { continue; }
            context.fillRect(column * scale, row * scale, scale, scale);
        }
    }
    context.globalAlpha = 1;

    // A hairline around the lit cells, so an all-light layer still has a
    // readable shape rather than being an absence.
    context.strokeStyle = options.accent;
    context.lineWidth = Math.max(1, scale * 0.14);
    for (let row = 0; row < size; row++) {
        for (let column = 0; column < size; column++) {
            const index = (row * size) + column;
            if (roles[index] !== options.highlight) { continue; }

            // Only edges facing OUT of the layer are stroked, so a block of
            // lit cells is outlined once rather than drawn as a mesh.
            const outside = (r, c) => r < 0 || c < 0 || r >= size || c >= size
                || roles[(r * size) + c] !== options.highlight;

            const x = column * scale, y = row * scale;
            context.beginPath();
            if (outside(row - 1, column)) { context.moveTo(x, y); context.lineTo(x + scale, y); }
            if (outside(row + 1, column)) { context.moveTo(x, y + scale); context.lineTo(x + scale, y + scale); }
            if (outside(row, column - 1)) { context.moveTo(x, y); context.lineTo(x, y + scale); }
            if (outside(row, column + 1)) { context.moveTo(x + scale, y); context.lineTo(x + scale, y + scale); }
            context.stroke();
        }
    }
}

/**
 * Draw the reading order over the code: the zigzag that IS "dot to dot".
 * `progress` is 0..1, so the caller can scrub it or animate it.
 */
function trace(canvas, grid, path, progress, options) {
    const scale = Math.max(1, Math.floor(options.pixels / grid.size));
    const context = canvas.getContext('2d');
    const upTo = Math.floor(path.length * progress);
    if (upTo < 2) { return; }

    const centre = point => ({
        x: (point.column * scale) + (scale / 2),
        y: (point.row * scale) + (scale / 2),
    });

    context.strokeStyle = options.accent;
    context.lineWidth = Math.max(1, scale * 0.35);
    context.lineJoin = 'round';
    context.lineCap = 'round';
    context.globalAlpha = 0.85;

    context.beginPath();
    const first = centre(path[0]);
    context.moveTo(first.x, first.y);
    for (let i = 1; i < upTo; i++) {
        const p = centre(path[i]);
        context.lineTo(p.x, p.y);
    }
    context.stroke();

    // The head of the snake, so the direction of travel is obvious when paused.
    const head = centre(path[upTo - 1]);
    context.globalAlpha = 1;
    context.fillStyle = options.accent;
    context.beginPath();
    context.arc(head.x, head.y, Math.max(1.5, scale * 0.7), 0, Math.PI * 2);
    context.fill();
}

export function render(canvas, grid, roles, path, state, options) {
    paint(canvas, grid, roles, Object.assign({ highlight: state.highlight }, options));
    if (state.tracing) { trace(canvas, grid, path, state.progress, options); }
}
